import Direction2D from './Direction2D';
import Point3D from './Point3D';
import { Rotation } from './rotation';
import {
  angleEquals,
  angleLessThan,
  angleLessThanOrEqual,
  convertRadiansToDegrees,
  distanceEquals,
  isDotProductZero,
  perigon,
  straightAngle,
} from './units';
import Vector3D from './Vector3D';

export interface Direction3DInterface {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** A unit vector in 3D space. */
class Direction3D {
  static readonly right = new Direction3D(1, 0, 0);
  static readonly left = new Direction3D(-1, 0, 0);
  static readonly up = new Direction3D(0, 1, 0);
  static readonly down = new Direction3D(0, -1, 0);
  static readonly positiveZ = new Direction3D(0, 0, 1);
  static readonly out = Direction3D.positiveZ;
  static readonly negativeZ = new Direction3D(0, 0, -1);
  static readonly in = Direction3D.negativeZ;

  readonly x: number;
  readonly y: number;
  readonly z: number;

  private constructor(x: number, y: number, z: number) {
    this.x = x;
    this.y = y;
    this.z = z;
  }

  // Methods for constructing directions from points in space whose coordinates are distances.
  static fromOriginToPoint(vector: Point3D) {
    if (distanceEquals(vector.magnitude, 0)) {
      return null;
    }
    const unitVector = vector.scaleToMagnitude(1);
    return new Direction3D(unitVector.x, unitVector.y, unitVector.z);
  }
  static fromPointToPoint(point0: Point3D, point1: Point3D) {
    return Direction3D.fromOriginToPoint(point1.subtract(point0));
  }

  // Create a direction from a unitless vector that is derived from operations on directions. Generally, for any given
  // code location where this is called, the vectors passed in from that location will have magnitude bounded by a small
  // constant, such as 1.
  static fromDirectionalVector(vector: Point3D) {
    if (isDotProductZero(vector.magnitude)) {
      return null;
    }
    const unitVector = vector.scaleToMagnitude(1);
    return new Direction3D(unitVector.x, unitVector.y, unitVector.z);
  }

  static fromObject(direction: Direction3DInterface) {
    return Direction3D.fromDirectionalVector(Point3D.fromObject(direction));
  }

  equals(other: Direction3D) {
    const angleBetween = this.angleBetween(other);
    return angleEquals(angleBetween, 0);
  }

  angleBetween(other: Direction3D) {
    const dotProduct = this.dotProduct(other);
    if (dotProduct < -1) return 180;
    if (dotProduct > 1) return 0;

    return convertRadiansToDegrees(Math.acos(dotProduct));
  }

  smallestAngleBetween(other: Direction3D) {
    const dotProductMagnitude = Math.abs(this.dotProduct(other));
    if (dotProductMagnitude > 1) return 0;

    return convertRadiansToDegrees(Math.acos(dotProductMagnitude));
  }

  /**
   * Gets the angle from this to the passed direction. The reference normal is the direction of the axis of
   * rotation for determining sign. A positive rotation about the positive z-axis produces a counterclockwise
   * rotation in the xy-plane. Considers which way the directions are pointing. The order of the directions matters.
   * @returns An angle between 0° and 360°, in degrees.
   */
  angleTo(direction: Direction3D, referenceNormal: Direction3D | null) {
    referenceNormal ??= Direction3D.out;
    const testAngle = this.angleBetween(direction);
    const testNormal = this.crossProductDirection(direction);
    if (angleEquals(testAngle, 0) || angleEquals(testAngle, straightAngle) || testNormal?.equals(referenceNormal))
      return testAngle;
    if (testNormal?.reverse().equals(referenceNormal)) return perigon - testAngle;
    throw new Error('The reference normal is not perpendicular to these vectors');
  }

  reverse() {
    return new Direction3D(-this.x, -this.y, -this.z);
  }

  isOrientedWith = (direction: Direction3D): boolean => angleLessThan(direction.angleBetween(this), 90);

  orientWith = (direction: Direction3D): Direction3D =>
    angleLessThanOrEqual(direction.angleBetween(this), 90) ? this : this.reverse();

  crossProductDirection(other: Direction3D) {
    return Direction3D.fromDirectionalVector(this.toPoint().crossProduct(other.toPoint()));
  }

  // Compute the component of this direction perpendicular to other.
  reject(other: Direction3D) {
    return Direction3D.fromDirectionalVector(this.toPoint().rejectDirection(other));
  }

  dotProduct(other: Direction3D) {
    return this.toPoint().dotProduct(other.toPoint());
  }

  projectTo2D(): Direction2D {
    return Direction2D.fromObject(this);
  }

  toPoint(scale = 1) {
    return new Point3D(this.x * scale, this.y * scale, this.z * scale);
  }

  toVector(scale: number) {
    return new Vector3D(this.x * scale, this.y * scale, this.z * scale);
  }

  toObject() {
    return { x: this.x, y: this.y, z: this.z };
  }

  reverseIf(condition: boolean) {
    return condition ? this.reverse() : this;
  }

  rotate(rotation: Rotation): Direction3D {
    const rotatedOrigin = Point3D.origin.rotate3D(rotation);
    const rotatedHead = this.toPoint().rotate3D(rotation);
    return rotatedHead.subtract(rotatedOrigin).toVector().direction!;
  }

  isParallelTo(other: Direction3D) {
    const angle = this.angleBetween(other);
    return angleEquals(angle, 0) || angleEquals(angle, 180);
  }

  isPerpendicularTo(other: Direction3D) {
    return angleEquals(this.angleBetween(other), 90);
  }
}

export default Direction3D;
